import * as React from "react";
import PropTypes from "prop-types";
import {useTheme} from "@mui/material/styles";
import MDBox from "components/MDBox";
import {
  GridRowModes,
  DataGrid,
  useGridApiRef,
  GridRowEditStopReasons,
  GridToolbar
} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  AppBar,
  Button,
  Card,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  Paper,
  Box,
  Tooltip
} from "@mui/material";
import {FirstPage, LastPage, NavigateBefore, NavigateNext} from "@mui/icons-material";
import {GET, PATCH, BASE_URL} from "APIclient";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import properties from "constants/ApplicationConstants";
import {formatEmployeeName, formatDesignation} from "constants/ApplicationConstants";
import copyableCell from "components/CopyableCell";
import * as XLSX from "xlsx";

const SERVER_OPTIONS = {
  useCursorPagination: false
};

const rowLength = 20;

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0"
      }}
    >
      <Typography variant="caption" sx={{color: "#666"}}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <Typography variant="caption" sx={{mr: 2, color: "#666"}}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton onClick={handleFirstPage} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton onClick={handlePreviousPage} disabled={page === 0 || loading} size="small">
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e"
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function EmployeeList() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [value, setValue] = React.useState(0);
  const [optionValue, setOptionValue] = React.useState(false);
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("error");
  const [page, setPage] = React.useState(0);
  const [pageSize, setSize] = React.useState(20);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [empId, setEmpId] = React.useState("");
  const [employeeName, setEmployeeName] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [filteredData, setFilteredData] = React.useState([]);
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedEmployee, setSelectedEmployee] = React.useState(null);
  const [downloadLoading, setDownloadLoading] = React.useState(false);

  const fetchData = async (page, pageSize, empId, employeeName, status, signal) => {
    try {
      setLoading(true);
      const apiEndpoint = status === "0" ? `admin/inactive-employees` : `admin/employees`;

      const response = await GET(
        `${apiEndpoint}?page=${page}&size=${pageSize}&empId=${empId}&empName=${employeeName}&status=${status}`,
        {signal}
      );
      let data = response?.data?.data?.paginatedData;
      const totalRecords = response?.data?.data?.totalItems;
      const totalPages = response?.data?.data?.totalPages;

      if (apiEndpoint === "admin/employees") {
        data = [...data].sort((a, b) => a.empId - b.empId);
      }

      const formattedData = data.map((item, index) => ({
        ...item,
        id: page * pageSize + index + 1,
        designation: formatDesignation(item.designation),
        employeeName: formatEmployeeName(item.employeeName)
      }));

      setData(formattedData);
      setTotalPages(totalPages);
      setTotalRows(totalRecords);
      setFilteredData(formattedData);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const apiRef = useGridApiRef();

  React.useEffect(() => {
    const controller = new AbortController();
    fetchData(page, pageSize, empId, employeeName, status, controller.signal);
    return () => controller.abort();
  }, [page, pageSize]);

  const handleSearch = async () => {
    setPage(0);
    if (status === "1") {
      setColumns(activeColumns);
    } else if (status === "0") {
      setColumns(inactiveColumns);
    }
    await fetchData(0, pageSize, empId, employeeName, status);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      await fetchData(lastPageIndex, pageSize, empId, employeeName, status);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  const onSearch = e => {
    let {value, name} = e.target;
    if (name === "empId") {
      setEmpId(value ? value : "");
      if (!value) {
        setPage(0);
        setEmployeeName("");
        setStatus("");
        setColumns(activeColumns);
        fetchData(0, pageSize, "", "", "");
      }
    } else if (name === "employeeName") {
      setEmployeeName(value ? value : "");
      if (!value) {
        setPage(0);
        setEmpId("");
        setStatus("");
        setColumns(activeColumns);
        fetchData(0, pageSize, "", "", "");
      }
    } else if (name === "status") {
      setStatus(value ? value : "");
      setPage(0);
      if (!value) {
        setColumns(activeColumns);
        fetchData(0, pageSize, empId, employeeName, "");
      }
    }
  };

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedEmployee(null);
  };

  const handleMarkAsSilent = async () => {
    if (!selectedEmployee) return;

    try {
      setLoading(true);
      const url = `admin/mark-silent`;
      const inputData = {empId: selectedEmployee.empId, silentStatus: 0};
      const response = await PATCH(url, inputData);

      if (response?.data?.statusCode === 200) {
        setSnakColor("success");
        setSnakMessage(response?.data?.message || "Employee marked as silent successfully.");
        setOpenDialog(false);
        setSelectedEmployee(null);
        fetchData(page, pageSize, empId, employeeName, status);
      } else {
        setSnakColor("error");
        setSnakMessage(response?.data?.message || "Failed to mark employee as silent.");
      }
    } catch (error) {
      console.error("Error marking employee as silent:", error);
      setSnakColor("error");
      setSnakMessage("Error marking employee as silent. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleView = (row, selectedOption) => {
    setOptionValue(selectedOption);
    setRowModesModel({...rowModesModel, [row.empId]: {mode: GridRowModes.Edit}});

    if (selectedOption === "Mark as Silent") {
      setSelectedEmployee(row);
      setOpenDialog(true);
    } else if (selectedOption === "View Profile") {
      navigate(`/profile`, {state: row.empId});
    } else if (selectedOption === "Edit Profile") {
      navigate(`/employee`, {state: row.empId});
    } else if (selectedOption === "Edit Permissions") {
      navigate(`/EditPermissions`, {state: row.empId});
    } else if (selectedOption === "Change Password") {
      navigate(`/changePassword`, {state: row.empId});
    } else if (selectedOption === "Add a Roaster") {
      navigate(`/AddToRoaster`, {state: row.empId});
    } else if (selectedOption === "Salary Revision") {
      navigate(`/salaryRevision`, {state: row.empId});
    } else if (selectedOption === "Reporting") {
      navigate(`/reporting`, {state: row.empId});
    } else if (selectedOption === "Exit") {
      navigate(`/exit`, {state: row.empId});
    } else if (selectedOption === "Appointment Letter") {
      navigate(`/appointmentLetter`, {state: row.empId});
    } else if (selectedOption === "Salary Certificate") {
      navigate(`/salaryCertificate`, {state: row.empId});
    } else if (selectedOption === "Address Certificate") {
      navigate(`/addressCertificate`, {state: row.empId});
    } else if (selectedOption === "Bonafide Certificate") {
      navigate(`/bonafideCertificate`, {state: row.empId});
    } else if (selectedOption === "Apply Resignation") {
      navigate(`/applyResignation`, {state: row.empId});
    } else if (selectedOption === "Assign Project") {
      navigate(`/assignProject`, {state: row.empId});
    } else if (selectedOption === "Hide Location") {
      navigate(`/hideLocation`, {state: row.empId});
    } else if (selectedOption === "CTC History") {
      navigate(`/ctc_History`, {state: row});
    } else if (selectedOption === "Print Experience Letter") {
      handlePrintExperienceLetter(row);
      setOptionValue("");
    } else if (selectedOption === "Export Experience Letter") {
      downloadExperienceLetter(row);
      setOptionValue("");
    } else if (selectedOption === "Print Relieving Letter") {
      handlePrintRelievingLetter(row);
      setOptionValue("");
    } else if (selectedOption === "Export Relieving Letter") {
      downloadRelievingLetter(row);
      setOptionValue("");
    } else if (selectedOption === "Reporting History") {
      navigate(`/reporting-history/${row.empId}`, {state: row});
    }
  };

  const handlePrintExperienceLetter = async row => {
    try {
      const url = `${BASE_URL}/admin/employee/downloadCertificate?type=experience_relieving_letter&search=${row.empId}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        },
        responseType: "blob",
        withCredentials: true
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch the PDF");
      }

      const fileBlob = new Blob([response.data], {type: "application/pdf"});
      const fileUrl = URL.createObjectURL(fileBlob);
      const newWindow = window.open(fileUrl, "_blank");
      if (!newWindow) {
        alert("Please allow pop-ups to view the confirmation letter.");
      }
    } catch (error) {
      console.error("Error fetching confirmation letter:", error);
      alert("Error generating the confirmation letter. Please try again.");
    }
  };

  const downloadExperienceLetter = async row => {
    try {
      const url = `${BASE_URL}/admin/employee/printCertificate?type=experience_relieving_letter&search=${row.empId}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        },
        responseType: "blob",
        withCredentials: true
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch the PDF");
      }
      const fileBlob = new Blob([response.data], {type: "application/pdf"});
      const fileUrl = URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `Experience_Letter_${row.empId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating experience letter:", error);
      alert("Error generating the experience letter. Please try again.");
    }
  };

  const handlePrintRelievingLetter = async row => {
    try {
      const url = `${BASE_URL}/admin/employee/downloadCertificate?type=reliving_letter&search=${row.empId}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        },
        responseType: "blob",
        withCredentials: true
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch the PDF");
      }

      const fileBlob = new Blob([response.data], {type: "application/pdf"});
      const fileUrl = URL.createObjectURL(fileBlob);
      const newWindow = window.open(fileUrl, "_blank");
      if (!newWindow) {
        alert("Please allow pop-ups to view the confirmation letter.");
      }
    } catch (error) {
      console.error("Error fetching confirmation letter:", error);
      alert("Error generating the confirmation letter. Please try again.");
    }
  };

  const downloadRelievingLetter = async row => {
    try {
      const url = `${BASE_URL}/admin/employee/printCertificate?type=reliving_letter&search=${row.empId}`;
      const response = await axios.get(url, {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        },
        responseType: "blob",
        withCredentials: true
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch the PDF");
      }
      const fileBlob = new Blob([response.data], {type: "application/pdf"});
      const fileUrl = URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `Relieving_Letter_${row.empId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating experience letter:", error);
      alert("Error generating the experience letter. Please try again.");
    }
  };

  const ITEM_HEIGHT = 20;
  const [options, setOptions] = React.useState([
    "View Profile",
    "Edit Profile",
    "Edit Permissions",
    "Change Password",
    "Add a Roaster",
    "Salary Revision",
    "Reporting",
    "Exit",
    "Appointment Letter",
    "Salary Certificate",
    "Address Certificate",
    "Bonafide Certificate",
    "Apply Resignation",
    "Assign Project",
    "Hide Location",
    "CTC History",
    "Mark as Silent"
  ]);

  const renderStatusChip = params => (
    <Chip
      size="small"
      label={params.value == 1 ? "Active" : "Inactive"}
      color={params.value === 1 ? "success" : "error"}
      sx={{
        backgroundColor: params.value === 1 ? "green" : "red",
        color: "#fff",
        fontWeight: "400"
      }}
    />
  );

  function formatDate(dateString) {
    if (!dateString) return " - ";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).format(date);
  }

  const [columns, setColumns] = React.useState([
    {field: "id", headerName: "#", width: 20, headerAlign: "left", align: "left"},
    {field: "empId", headerName: "EMP ID", width: 70, headerAlign: "left", align: "left"},
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 180,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "officialEmailAddress",
      headerName: "Official ID",
      width: 180,
      renderCell: copyableCell
    },
    {
      field: "emailAddress",
      headerName: "Personal ID",
      width: 180,
      renderCell: copyableCell
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 150,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "joinDate",
      headerName: "Joining Date",
      width: 130,
      renderCell: params => formatDate(params.value),
      headerAlign: "left",
      align: "left"
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      headerAlign: "left",
      align: "left",
      cellClassName: "actions",
      renderCell: params => (
        <div>
          <Select
            variant="standard"
            key={`menu-${params.row.empId}`}
            value={optionValue}
            onChange={event => handleView(params.row, event.target.value)}
            displayEmpty
            defaultValue=""
            style={{width: "1px"}}
            renderValue={selected => {
              if (!selected) {
                return (
                  <span style={{display: "flex", alignItems: "center"}}>
                    <MoreVertIcon style={{marginRight: "8px", width: ""}} />
                  </span>
                );
              }
              return selected;
            }}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {options?.map((option, index) => (
              <MenuItem key={index} value={option}>
                {option ? option : <MoreVertIcon />}
              </MenuItem>
            ))}
          </Select>
        </div>
      )
    }
  ]);

  let activeColumns = [
    {field: "id", headerName: "#", width: 20, headerAlign: "left", align: "left"},
    {field: "empId", headerName: "Employee ID", width: 110, headerAlign: "left", align: "left"},
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 160,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "officialEmailAddress",
      headerName: "Official ID",
      width: 160,
      renderCell: copyableCell
    },
    {
      field: "emailAddress",
      headerName: "Personal ID",
      width: 150,
      renderCell: copyableCell
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 150,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "joinDate",
      headerName: "Joining Date",
      width: 130,
      align: "left",
      headerAlign: "left",
      renderCell: params => formatDate(params.value)
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      headerAlign: "left",
      align: "left",
      cellClassName: "actions",
      renderCell: params => (
        <div>
          <Select
            variant="standard"
            key={`menu-${params.row.empId}`}
            value={optionValue}
            onChange={event => handleView(params.row, event.target.value)}
            displayEmpty
            defaultValue=""
            style={{width: "1px"}}
            renderValue={selected => {
              if (!selected) {
                return (
                  <span style={{display: "flex", alignItems: "center"}}>
                    <MoreVertIcon style={{marginRight: "8px", width: ""}} />
                  </span>
                );
              }
              return selected;
            }}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {options?.map((option, index) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </div>
      )
    }
  ];

  const inactiveColumns = [
    {field: "id", headerName: "#", width: 20, headerAlign: "left", align: "left"},
    {field: "empId", headerName: "Employee ID", width: 110, headerAlign: "left", align: "left"},
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 160,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "emailAddress",
      headerName: "Personal ID",
      width: 160,
      renderCell: copyableCell
    },
    {
      field: "joinDate",
      headerName: "Joining Date",
      width: 130,
      headerAlign: "left",
      align: "left",
      renderCell: params => formatDate(params.value)
    },
    {
      field: "resignationDate",
      headerName: "Resignation Date",
      width: 150,
      headerAlign: "left",
      align: "left",
      renderCell: params => formatDate(params.value)
    },
    {
      field: "leavingDate",
      headerName: "Leaving Date",
      width: 150,
      headerAlign: "left",
      align: "left",
      renderCell: params => formatDate(params.value)
    },
    {
      field: "reasonForLeaving",
      headerName: "METHOD",
      width: 150,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "plannedLeave",
      headerName: "PL",
      width: 100,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "unplannedLeave",
      headerName: "UL",
      width: 100,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "coffLeave",
      headerName: "Coff Leaves",
      width: 110,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      headerAlign: "left",
      align: "left",
      cellClassName: "actions",
      renderCell: params => {
        const isInactive = params.row.status === 0;
        const rowOptions = isInactive
          ? [
              "View Profile",
              "Print Experience Letter",
              "Print Relieving Letter",
              "Export Experience Letter",
              "Export Relieving Letter",
              "CTC History",
              "Reporting History"
            ]
          : [
              "View Profile",
              "Edit Profile",
              "Edit Permissions",
              "Change Password",
              "Add a Roaster",
              "Salary Revision",
              "Reporting",
              "Exit",
              "Appointment Letter",
              "Salary Certificate",
              "Address Certificate",
              "Bonafide Certificate",
              "Apply Resignation",
              "Assign Project",
              "Hide Location",
              "CTC History",
              "Mark as Silent"
            ];

        return (
          <div>
            <Select
              variant="standard"
              key={`menu-${params.row.empId}`}
              value=""
              onChange={event => handleView(params.row, event.target.value)}
              displayEmpty
              style={{width: "1px"}}
              renderValue={selected => {
                if (!selected) {
                  return (
                    <span style={{display: "flex", alignItems: "center"}}>
                      <MoreVertIcon style={{marginRight: "8px"}} />
                    </span>
                  );
                }
                return selected;
              }}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {rowOptions.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </div>
        );
      }
    }
  ];

  const handleDownloadExcel = async () => {
    try {
      setDownloadLoading(true);
      const apiEndpoint = status === "0" ? `admin/inactive-employees` : `admin/employees`;

      const response = await GET(
        `${apiEndpoint}?page=0&size=${
          totalRows || 10000
        }&empId=${empId}&empName=${employeeName}&status=${status}`
      );

      const rawData = response?.data?.data?.paginatedData || [];

      let exportData;

      if (status === "0") {
        // Inactive employees
        exportData = rawData.map((item, index) => ({
          "SR.No": index + 1,
          "Employee ID": item.empId,
          "Employee Name": formatEmployeeName(item.employeeName),
          "Personal Email": item.emailAddress || "N/A",
          "Joining Date": item.joinDate ? formatDate(item.joinDate) : "N/A",
          "Resignation Date": item.resignationDate ? formatDate(item.resignationDate) : "N/A",
          "Leaving Date": item.leavingDate ? formatDate(item.leavingDate) : "N/A",
          Method: item.reasonForLeaving || "N/A",
          PL: item.plannedLeave ?? "N/A",
          UL: item.unplannedLeave ?? "N/A",
          "Coff Leaves": item.coffLeave ?? "N/A"
        }));
      } else {
        // Active employees (default)
        exportData = rawData.map((item, index) => ({
          "SR.No": index + 1,
          "Employee ID": item.empId,
          "Employee Name": formatEmployeeName(item.employeeName),
          "Official Email": item.officialEmailAddress || "N/A",
          "Personal Email": item.emailAddress || "N/A",
          Designation: formatDesignation(item.designation) || "N/A",
          "Joining Date": item.joinDate ? formatDate(item.joinDate) : "N/A"
        }));
      }

      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Auto-fit column widths
      worksheet["!cols"] = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));

      const workbook = XLSX.utils.book_new();
      const sheetName = status === "0" ? "Inactive Employees" : "Active Employees";
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      const fileName = status === "0" ? "Inactive_Employees.xlsx" : "Active_Employees.xlsx";
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Error downloading employee data:", error);
      setSnakColor("error");
      setSnakMessage("Failed to download employee data. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(12, 1fr)",
                  gap: 1,
                  alignItems: "center"
                }}
              >
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Employee List
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={empId}
                  onChange={e => onSearch(e)}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="empId"
                  label="Employee ID"
                  variant="outlined"
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={employeeName}
                  onChange={e => onSearch(e)}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="employeeName"
                  label="Employee Name"
                  variant="outlined"
                />
                <FormControl variant="outlined" sx={{m: 0, width: "15ch", gridColumn: "span 2"}}>
                  <InputLabel id="Status">Status</InputLabel>
                  <Select
                    labelId="status"
                    id="status"
                    name="status"
                    value={status}
                    onChange={e => onSearch(e)}
                    onKeyDown={event => {
                      if (event.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    label="Status"
                    sx={{minWidth: 150, p: 1.5}}
                  >
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Inactive</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    fontFamily: properties.fontFamily,
                    background: properties.colors.bgcolorbluethem
                  }}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>

                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    fontFamily: properties.fontFamily,
                    background: "#2e7d32" 
                  }}
                  variant="contained"
                  color="success"
                  onClick={handleDownloadExcel}
                  disabled={downloadLoading}
                >
                  {downloadLoading ? "Downloading..." : "Download Excel"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        <MDBox sx={{overflow: "auto"}}>
          <Paper>
            <DataGrid
              rows={filteredData}
              columns={columns}
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[20]}
              paginationModel={{page, pageSize}}
              // onPaginationModelChange={handlePaginationChange}
              loading={loading}
              getRowId={row => row.empId}
              components={{Toolbar: GridToolbar}}
              rowModesModel={rowModesModel}
              apiRef={apiRef}
              hideFooter
              sortingMode="server"
              sortModel={[]}
              sx={{
                fontFamily: properties.fontFamily,
                fontSize: "14px",
                fontWeight: 400,
                height: "450px",
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important"
                }
                // "& .MuiDataGrid-columnHeaders": {
                //   position: "sticky",
                //   top: 0,
                //   backgroundColor: "white",
                //   zIndex: 1000
                // },
                // "& .MuiDataGrid-virtualScroller": {
                //   overflow: "auto"
                // },
                // "& .MuiDataGrid-root": {
                //   height: "auto",
                //   maxHeight: "600px"
                // },
                // "& .MuiDataGrid-cell": {
                //   textAlign: "left"
                // }
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>

        <Dialog
          open={openDialog}
          onClose={handleDialogClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle
            id="alert-dialog-title"
            sx={{fontSize: "16px", fontFamily: properties.fontFamily}}
          >
            Confirm Mark as Silent
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id="alert-dialog-description"
              sx={{fontSize: "14px", fontFamily: properties.fontFamily}}
            >
              Are you sure you want to mark this employee as silent?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDialogClose}
              color="primary"
              sx={{fontFamily: properties.fontFamily}}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkAsSilent}
              color="primary"
              sx={{fontFamily: properties.fontFamily}}
              autoFocus
              disabled={loading}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snakColor !== ""}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={() => setSnakColor("")}
        >
          <Alert
            onClose={() => setSnakColor("")}
            severity={snakColor}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snakMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}
